import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import Layout from '../components/Layout';
import ReactCountryFlag from "react-country-flag";
import { ArrowLeft, ArrowRightLeft, TrendingUp, Activity, Clock } from 'lucide-react';

const COUNTRY_NAMES = {
  'VE': 'Venezuela',
  'CO': 'Colombia',
  'PE': 'Perú',
  'EC': 'Ecuador',
  'US': 'EE.UU.', // Nombre más corto para que quepa bien en la cuadrícula de 5
  'PA': 'Panamá',
  'MX': 'México',
  'CL': 'Chile'
};

const RatesPage = () => {
  const navigate = useNavigate();
  const [rates, setRates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrigin, setSelectedOrigin] = useState('');
  const [origins, setOrigins] = useState([]);

  useEffect(() => {
    fetchRates();
  }, []);

  const fetchRates = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('rates')
        .select('*')
        .order('origin_country', { ascending: true });

      if (error) throw error;

      if (data && data.length > 0) {
        setRates(data);
        const uniqueOrigins = [...new Set(data.map(r => r.origin_country))];
        setOrigins(uniqueOrigins);
        setSelectedOrigin(uniqueOrigins[0]);
      }
    } catch (err) {
      console.error('Error cargando tasas:', err);
    } finally {
      setLoading(false);
    }
  };

  const filteredRates = rates.filter(r => r.origin_country === selectedOrigin);

  return (
    <Layout>
      <div className="min-h-screen bg-[#f4f7fb] pb-24 relative overflow-hidden">
        
        {/* 🔥 DECORACIÓN PREMIUM AJUSTADA: Más alta para cubrir el selector */}
        <div className="absolute top-0 left-0 w-full h-[520px] md:h-[550px] bg-gradient-to-b from-blue-900 via-blue-800 to-[#f4f7fb] pointer-events-none z-0"></div>
        <div className="absolute top-0 left-0 w-full h-[520px] md:h-[550px] opacity-[0.15] bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-white via-transparent to-transparent pointer-events-none z-0"></div>

        <div className="relative z-10 max-w-[1400px] mx-auto px-4 sm:px-6 pt-8">
          
          {/* HEADER */}
          <motion.div 
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            className="flex items-center justify-between mb-10"
          >
            <button
              onClick={() => navigate(-1)}
              className="w-10 h-10 bg-white/10 backdrop-blur-md border border-white/20 rounded-full flex items-center justify-center text-white hover:bg-white/20 transition-all shadow-sm active:scale-95"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>

            <div className="flex items-center gap-2 bg-white/10 backdrop-blur-md border border-white/20 px-4 py-2 rounded-full shadow-sm">
              <span className="relative flex h-2.5 w-2.5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-green-500"></span>
              </span>
              <span className="text-[10px] font-black text-white uppercase tracking-widest">Tasas en Vivo</span>
            </div>
          </motion.div>

          {/* TÍTULO */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1, duration: 0.6, ease: "easeOut" }}
            className="text-center mb-12"
          >
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-black text-white tracking-tight mb-4 drop-shadow-lg">
              Mercado Global
            </h1>
            <p className="text-blue-100/80 font-medium text-sm md:text-base max-w-xl mx-auto flex items-center justify-center gap-2">
              <Activity className="w-4 h-4" />
              Tasas de cambio actualizadas al instante
            </p>
          </motion.div>

          {/* 🌟 SELECTOR DE PAÍS ACTUALIZADO (Cuadrícula de 5 fija) */}
          {origins.length > 0 && (
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.6 }}
              className="mb-12 flex flex-col items-center"
            >
              <p className="text-white/60 text-[10px] font-black uppercase tracking-[0.2em] mb-5 text-center">
                Selecciona tu país de envío
              </p>
              
              {/* 🔥 Grid de 5 columnas fija para móvil y PC */}
              <div className="grid grid-cols-5 gap-2 sm:gap-3 w-full max-w-3xl">
                {origins.map((origin) => {
                  const isSelected = selectedOrigin === origin;
                  return (
                    <button
                      key={origin}
                      onClick={() => setSelectedOrigin(origin)}
                      className={`group relative flex flex-col items-center gap-2 p-3 sm:p-4 rounded-2xl transition-all duration-300 ease-out active:scale-95 h-full ${
                        isSelected 
                          ? 'bg-white shadow-[0_8px_30px_rgba(255,255,255,0.2)] scale-105 z-10' 
                          : 'bg-white/10 backdrop-blur-md border border-white/10 hover:bg-white/20 hover:border-white/30 text-white'
                      }`}
                    >
                      {/* Banderas ligeramente más pequeñas para la cuadrícula de 5 */}
                      <div className={`transition-transform duration-300 ${isSelected ? 'scale-110' : 'opacity-90 group-hover:scale-105'}`}>
                        <ReactCountryFlag 
                          countryCode={origin} 
                          svg 
                          style={{ width: '1.2em', height: '1.2em', borderRadius: '50%', objectFit: 'cover' }} 
                        />
                      </div>
                      
                      {/* Nombre del país centrado y ajustado */}
                      <span className={`font-black uppercase text-[9px] sm:text-xs tracking-wider text-center break-words transition-colors duration-300 ${
                        isSelected ? 'text-blue-900' : 'text-white/90'
                      }`}>
                        {COUNTRY_NAMES[origin] || origin}
                      </span>
                      
                      {/* Borde animado sutil cuando está seleccionado */}
                      {isSelected && (
                        <motion.div
                          layoutId="selectedBorder"
                          className="absolute inset-0 border-2 border-blue-400 rounded-2xl pointer-events-none"
                          initial={false}
                          transition={{ type: "spring", stiffness: 400, damping: 30 }}
                        />
                      )}
                    </button>
                  );
                })}
              </div>
            </motion.div>
          )}

          {/* GRID DE RESULTADOS */}
          {loading ? (
            <div className="flex flex-col items-center justify-center py-20">
              <div className="w-12 h-12 border-4 border-white/20 border-t-white rounded-full animate-spin mb-4 shadow-lg"></div>
              <p className="text-white/60 font-bold uppercase tracking-widest text-xs animate-pulse">Consultando mercado...</p>
            </div>
          ) : (
            <motion.div layout className="relative min-h-[400px]">
              <AnimatePresence mode="wait">
                <motion.div
                  key={selectedOrigin}
                  initial={{ opacity: 0, filter: "blur(4px)" }}
                  animate={{ opacity: 1, filter: "blur(0px)" }}
                  exit={{ opacity: 0, filter: "blur(4px)" }}
                  transition={{ duration: 0.4, ease: "easeInOut" }}
                  className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6"
                >
                  {filteredRates.map((rate) => (
                    <div
                      key={rate.id}
                      className="bg-white rounded-[24px] p-5 shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:shadow-[0_20px_40px_rgb(37,99,235,0.08)] border border-slate-100 transition-all duration-300 group relative overflow-hidden flex flex-col h-full"
                    >
                      <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-br from-blue-50/50 to-transparent rounded-bl-full opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"></div>

                      {/* BANDERAS Y DIRECCIÓN */}
                      <div className="flex items-center justify-between mb-6 relative z-10">
                        <div className="flex flex-col items-center gap-1.5">
                          <div className="w-10 h-10 bg-slate-50 rounded-xl flex items-center justify-center border border-slate-100 shadow-inner">
                            <ReactCountryFlag countryCode={rate.origin_country} svg style={{ width: '1.5em', height: '1.5em', borderRadius: '4px' }} />
                          </div>
                          <span className="text-[9px] font-black uppercase tracking-widest text-slate-400">{rate.origin_country}</span>
                        </div>

                        <div className="flex-1 flex items-center justify-center px-2">
                          <ArrowRightLeft className="w-3.5 h-3.5 text-blue-300/50" />
                        </div>

                        <div className="flex flex-col items-center gap-1.5">
                          <div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center border border-blue-100 shadow-inner">
                            <ReactCountryFlag countryCode={rate.destination_country} svg style={{ width: '1.5em', height: '1.5em', borderRadius: '4px' }} />
                          </div>
                          <span className="text-[9px] font-black uppercase tracking-widest text-blue-500">{rate.destination_country}</span>
                        </div>
                      </div>

                      {/* VALOR DE LA TASA */}
                      <div className="flex-1 flex flex-col justify-center items-center text-center relative z-10 mb-4">
                        <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mb-1.5">Recibes</p>
                        <div className="flex items-baseline gap-1.5">
                          <h3 className="text-2xl sm:text-3xl font-black text-slate-800 tracking-tighter">
                            {Number(rate.rate).toLocaleString('es-CO', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}
                          </h3>
                        </div>
                        <span className="text-xs font-black text-blue-600 uppercase mt-0.5 tracking-wider">
                          {rate.currency}
                        </span>
                      </div>

                      {/* FOOTER DE LA CARD */}
                      <div className="mt-auto pt-3 border-t border-slate-50 flex items-center justify-between relative z-10">
                        <div className="flex items-center gap-1 text-slate-400">
                          <Clock className="w-3 h-3" />
                          <span className="text-[8px] font-bold uppercase tracking-widest">
                            {new Date(rate.updated_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          </span>
                        </div>
                        <TrendingUp className="w-3 h-3 text-slate-300 group-hover:text-blue-500 transition-colors duration-300" />
                      </div>
                    </div>
                  ))}
                </motion.div>
              </AnimatePresence>
            </motion.div>
          )}

        </div>
      </div>
    </Layout>
  );
};

export default RatesPage;

